// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 07-source-control-pipeline.md §Re-install semantics
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { OrgSettings } from '@greenlight/shared';
import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';

import { SettingsSectionHeading } from './SettingsSectionHeading';
import {
  SourceControlMigration,
  type MigrationExecuteResult,
  type MigrationPreviewResult,
} from './SourceControlMigration';

/** Result of an on-demand managed-repo access check (verifySourceControl). */
export interface VerifyRepoAccessResult {
  ok: boolean;
  accessible: string[];
  missing: string[];
  /** Surfaced server message when the check itself couldn't run (e.g. suspended). */
  error?: string;
}

// ── Source-control status → badge map ────────────────────────────────────────

type ScmStatus = NonNullable<OrgSettings['source_control']>['status'];

const SCM_STATUS: Record<ScmStatus, { variant: StatusBadgeVariant; label: string }> = {
  connected: { variant: 'active', label: 'Connected' },
  suspended: { variant: 'degraded', label: 'Suspended' },
  error: { variant: 'failed', label: 'Error' },
};

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SourceControlSettingsProps {
  sourceControl: OrgSettings['source_control'];
  onOverrideScanner?: (name: string) => void;
  /** On-demand managed-repo access check. */
  onVerify: () => Promise<VerifyRepoAccessResult>;
  /** Re-run the GitHub App manifest flow; navigates to GitHub on success. */
  onRecreate: () => Promise<void> | void;
  /** Org-migration dry run / execute (passed through to the migration panel). */
  onPreviewMigration: () => Promise<MigrationPreviewResult>;
  onExecuteMigration: (targetOrgHandle: string) => Promise<MigrationExecuteResult>;
  className?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export function SourceControlSettings({
  sourceControl,
  onOverrideScanner,
  onVerify,
  onRecreate,
  onPreviewMigration,
  onExecuteMigration,
  className,
}: SourceControlSettingsProps): React.JSX.Element {
  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Source control" pill="Org admin" />

      {/* Unconfigured state */}
      {sourceControl === undefined ? (
        <SectionCard headerBar title="Source control">
          <div className="px-5 py-4 text-sm text-muted-foreground">Not configured yet.</div>
        </SectionCard>
      ) : (
        <ConfiguredSourceControl
          sourceControl={sourceControl}
          onOverrideScanner={onOverrideScanner}
          onVerify={onVerify}
          onRecreate={onRecreate}
          onPreviewMigration={onPreviewMigration}
          onExecuteMigration={onExecuteMigration}
        />
      )}
    </div>
  );
}

// ── Configured source control cards ──────────────────────────────────────────

interface ConfiguredSourceControlProps {
  sourceControl: NonNullable<OrgSettings['source_control']>;
  onOverrideScanner?: (name: string) => void;
  onVerify: () => Promise<VerifyRepoAccessResult>;
  onRecreate: () => Promise<void> | void;
  onPreviewMigration: () => Promise<MigrationPreviewResult>;
  onExecuteMigration: (targetOrgHandle: string) => Promise<MigrationExecuteResult>;
}

function ConfiguredSourceControl({
  sourceControl,
  onOverrideScanner,
  onVerify,
  onRecreate,
  onPreviewMigration,
  onExecuteMigration,
}: ConfiguredSourceControlProps): React.JSX.Element {
  return (
    <>
      {/* ── Card 1: GitHub App + lifecycle actions ─────────────────────────── */}
      <GitHubAppCard sourceControl={sourceControl} onVerify={onVerify} onRecreate={onRecreate} />

      {/* ── Card 2: Branch policy (read-only) ─────────────────────────────── */}
      <BranchPolicyCard sourceControl={sourceControl} />

      {/* ── Card 3: Scanner pins ──────────────────────────────────────────── */}
      <ScannerPinsCard
        scannerPins={sourceControl.scanner_pins}
        onOverrideScanner={onOverrideScanner}
      />

      {/* ── Card 4: Org migration (docs/07 §Re-install semantics) ─────────── */}
      <SourceControlMigration onPreview={onPreviewMigration} onExecute={onExecuteMigration} />
    </>
  );
}

// ── Card 1: GitHub App ────────────────────────────────────────────────────────

interface GitHubAppCardProps {
  sourceControl: NonNullable<OrgSettings['source_control']>;
  onVerify: () => Promise<VerifyRepoAccessResult>;
  onRecreate: () => Promise<void> | void;
}

function GitHubAppCard({
  sourceControl,
  onVerify,
  onRecreate,
}: GitHubAppCardProps): React.JSX.Element {
  const [verifying, setVerifying] = React.useState(false);
  const [verifyResult, setVerifyResult] = React.useState<VerifyRepoAccessResult | null>(null);
  const [recreating, setRecreating] = React.useState(false);

  async function handleVerify(): Promise<void> {
    if (verifying) return;
    setVerifying(true);
    try {
      setVerifyResult(await onVerify());
    } finally {
      setVerifying(false);
    }
  }

  async function handleRecreate(): Promise<void> {
    if (recreating) return;
    setRecreating(true);
    // On success this navigates to GitHub (manifest form POST), so the spinner
    // persists through the redirect; reset only if it returns without leaving.
    try {
      await onRecreate();
    } finally {
      setRecreating(false);
    }
  }

  return (
    <div className="rounded-gl-lg border border-border bg-card overflow-hidden">
      {/* Connected-app row */}
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* GitHub mark tile */}
          <span
            aria-hidden="true"
            className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-gl-icon border border-border bg-card"
            style={{ width: 40, height: 40 }}
          >
            <img src="/brands/github.svg" alt="" width={24} height={24} aria-hidden="true" />
          </span>

          {/* Name + installation sub-line */}
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[13px] font-bold text-foreground leading-tight">
              Greenlight GitHub App
            </span>
            <span className="font-mono text-[11px] text-muted-foreground leading-tight">
              Installed on <span className="text-foreground">{sourceControl.account}</span>
              {' · installation '}
              <span className="text-foreground">{sourceControl.installation_id}</span>
            </span>
          </div>
        </div>
        <StatusBadge variant={SCM_STATUS[sourceControl.status].variant}>
          {SCM_STATUS[sourceControl.status].label}
        </StatusBadge>
      </div>

      {/* Verify result */}
      {verifyResult ? (
        <div className="border-t border-border px-5 py-3" data-testid="scm-verify-result">
          {verifyResult.error ? (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t verify repo access</AlertTitle>
              <AlertDescription>{verifyResult.error}</AlertDescription>
            </Alert>
          ) : verifyResult.missing.length > 0 ? (
            <Alert variant="destructive">
              <AlertTitle>
                {verifyResult.missing.length} managed{' '}
                {verifyResult.missing.length === 1 ? 'repo is' : 'repos are'} unreachable
              </AlertTitle>
              <AlertDescription>
                Add{' '}
                {verifyResult.missing.map((r, i) => (
                  <React.Fragment key={r}>
                    {i > 0 ? ', ' : ''}
                    <span className="font-mono">{r}</span>
                  </React.Fragment>
                ))}{' '}
                to the installation&apos;s repository selection on GitHub.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTitle>All {verifyResult.accessible.length} managed repos reachable</AlertTitle>
              <AlertDescription>
                The installation can see every repo Greenlight manages.
              </AlertDescription>
            </Alert>
          )}
        </div>
      ) : null}

      {/* Hairline + description + actions */}
      <div className="border-t border-border px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Greenlight reads repos and writes pipeline checks through this installation.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleVerify()}
            disabled={verifying}
            className="h-11 md:h-9"
            data-testid="scm-verify"
          >
            <RefreshCw className={cn('h-4 w-4', verifying && 'animate-spin')} aria-hidden="true" />
            {verifying ? 'Verifying…' : 'Verify repo access'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleRecreate()}
            disabled={recreating}
            className="h-11 md:h-9"
            data-testid="scm-recreate"
          >
            {recreating ? 'Opening GitHub…' : 'Re-create App'}
          </Button>
          <a
            href="https://github.com/apps"
            target="_blank"
            rel="noreferrer"
            aria-label="Manage Greenlight GitHub App on GitHub (opens in new tab)"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground h-11 md:h-9"
          >
            Manage on GitHub
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Re-create caveat */}
      <p className="border-t border-border px-5 py-2 text-xs text-muted-foreground">
        Re-creating replaces the App on GitHub and rotates its secrets — use it after a key
        compromise or control-plane URL change. The current App keeps working until you finish on
        GitHub; the old App is then flagged for manual deletion.
      </p>
    </div>
  );
}

// ── Card 2: Branch policy (informational) ─────────────────────────────────────

interface BranchPolicyCardProps {
  sourceControl: NonNullable<OrgSettings['source_control']>;
}

function BranchPolicyCard({ sourceControl }: BranchPolicyCardProps): React.JSX.Element {
  return (
    <SectionCard headerBar title="Branch policy">
      <p className="border-b border-border px-5 py-3 text-xs text-muted-foreground">
        Enforced by Greenlight on every app repo at registration. Not configurable here.
      </p>
      <div className="divide-y divide-border">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5">
          <span className="text-sm text-foreground">Default branch</span>
          <MonoValue className="max-w-[200px] truncate" title={sourceControl.default_branch}>
            {sourceControl.default_branch}
          </MonoValue>
        </div>

        <PolicyStatusRow
          label="Require pull request before deploy"
          helper="No direct pushes ship to production"
          enforced={sourceControl.require_pr}
        />

        <PolicyStatusRow
          label="Require all checks to pass"
          helper="Pipeline must be green to merge"
          enforced={sourceControl.require_checks}
        />
      </div>
    </SectionCard>
  );
}

interface PolicyStatusRowProps {
  label: string;
  helper: string;
  enforced: boolean;
}

function PolicyStatusRow({ label, helper, enforced }: PolicyStatusRowProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm text-foreground">{label}</span>
        <p className="text-xs text-muted-foreground">{helper}</p>
      </div>
      <StatusBadge variant={enforced ? 'active' : 'inactive'}>
        {enforced ? 'Enforced' : 'Not enforced'}
      </StatusBadge>
    </div>
  );
}

// ── Card 3: Scanner pins ──────────────────────────────────────────────────────

interface ScannerPinsCardProps {
  scannerPins: NonNullable<OrgSettings['source_control']>['scanner_pins'];
  onOverrideScanner?: (name: string) => void;
}

function ScannerPinsCard({
  scannerPins,
  onOverrideScanner,
}: ScannerPinsCardProps): React.JSX.Element {
  return (
    <SectionCard
      headerBar
      title="Scanner pins"
      badge={
        <span className="font-mono text-[11px] text-muted-foreground">pinned by Greenlight</span>
      }
    >
      <div className="divide-y divide-border">
        {scannerPins.map((pin) => (
          <div key={pin.name} className="flex items-center justify-between px-5 py-3.5">
            {/* Scanner name */}
            <span className="text-sm text-foreground">{pin.name}</span>

            {/* Version chip + Override */}
            <div className="flex items-center gap-3 shrink-0">
              <MonoValue title={pin.version}>{pin.version}</MonoValue>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOverrideScanner?.(pin.name)}
                aria-label={`Override ${pin.name} pin`}
                className="h-11 md:h-9"
              >
                Override
              </Button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
